import React, { useEffect, useRef, VFC } from 'react';
import { css } from '@emotion/css';
import gsap from 'gsap';
import { PALETTE_HEX } from '../constants/palettes';

type OpeningLoadingProps = {
  onComplete: () => void;
};

export const OpeningLoading: VFC<OpeningLoadingProps> = ({ onComplete }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  
  // 使用 ref 来存储回调，避免依赖变化导致动画重置
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // 获取当前主题色 (P1Home 的配色)
    const [mainColor, secondaryColor] = PALETTE_HEX[0];

    const tl = gsap.timeline({
      onComplete: () => {
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    });

    // 初始状态
    gsap.set([topRef.current, bottomRef.current], {
      width: '100%',
      height: '50vh',
      backgroundColor: '#fff',
      opacity: 1,
      position: 'absolute',
      left: 0,
      zIndex: 2,
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden'
    });

    gsap.set(topRef.current, { 
      top: 0,
      transformOrigin: 'bottom center',
      rotateX: 0
    });
    
    gsap.set(bottomRef.current, { 
      bottom: 0,
      transformOrigin: 'top center',
      rotateX: 0
    });

    gsap.set(lineRef.current, {
      width: '0%',
      height: '3px',
      opacity: 1,
      // 使用主题色渐变
      background: `linear-gradient(90deg, rgba(255,255,255,0) 0%, ${mainColor} 50%, ${secondaryColor} 100%)`,
      left: 0,
      top: '50%',
      y: '-50%',
      position: 'absolute',
      zIndex: 3
    });

    // 动画序列
    tl.to(lineRef.current, {
      width: '100%',
      duration: 1.0,
      ease: 'power3.inOut'
    })
    // 保持展示一小会儿
    .to({}, { duration: 0.2 })
    // 线条展开成开门效果
    .to(lineRef.current, {
      scaleY: 0,
      opacity: 0,
      duration: 0.4,
      ease: 'power2.in'
    })
    .to(topRef.current, {
      rotateX: -100,
      y: -300,
      z: 200,
      opacity: 0,
      borderBottomLeftRadius: '100% 30%',
      borderBottomRightRadius: '100% 30%',
      filter: 'blur(20px)',
      duration: 1.2, // 加快速度
      ease: 'power2.inOut'
    }, '-=0.3')
    .to(bottomRef.current, {
      rotateX: 100,
      y: 300,
      z: 200,
      opacity: 0,
      borderTopLeftRadius: '100% 30%',
      borderTopRightRadius: '100% 30%',
      filter: 'blur(20px)',
      duration: 1.2, // 加快速度
      ease: 'power2.inOut'
    }, '<');

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <div className={styles.container} ref={containerRef}>
      <div ref={topRef} />
      <div ref={bottomRef} />
      <div className={styles.line} ref={lineRef} />
    </div>
  );
};

const styles = {
  container: css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    perspective: 800px;
    pointer-events: none;
    overflow: hidden;
  `,
  line: css`
    z-index: 10;
  `
};
